import {exportBarSegments, exportTotalRecords} from './attendanceExportFigures'
import {resolveRingCenter} from './attendanceRingCenter'
import {getStatusTheme} from './attendanceStatusTheme'
import {AttendanceVisualSummary} from './buildAttendanceVisualSummary'

const WIDTH = 500
const HEIGHT = 120
/** The bitmap is placed at 170mm ≈ 6.7in in the PDF, so a 500px canvas lands at ~75 DPI and every
 *  edge and glyph is upscaled about 4× on the page — the "broken pixelette" look. Rasterise at 4×
 *  (≈300 DPI, what print wants) and scale the context, so every coordinate below stays in the same
 *  500×120 logical space. */
const SCALE = 4
const BAR_TOP = 18
const BAR_H = 14
const BAR_GAP = 6

/** Donut + status bars as PNG for PDF export (no extra packages). */
export function renderAttendanceSummaryChartPng(
  summary: AttendanceVisualSummary | null | undefined
): string | null {
  if (!summary || summary.mode === 'stats' || summary.statusSegments.length === 0) {
    return null
  }

  if (typeof document === 'undefined') return null

  // The on-screen bars, Checkout included, plus one row for the total.
  const bars = exportBarSegments(summary)
  const chartHeight = Math.max(HEIGHT, BAR_TOP + (bars.length + 1) * (BAR_H + BAR_GAP))

  const canvas = document.createElement('canvas')
  canvas.width = WIDTH * SCALE
  canvas.height = chartHeight * SCALE
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(SCALE, SCALE)

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, WIDTH, chartHeight)

  // Same rule as the on-screen ring: the donut is the rate's denominator, so statuses outside the
  // rate (Unmarked, Checkout) are not slices of it — otherwise the exported chart shows an arc that
  // disagrees with the percentage printed in its own middle.
  const chartable = summary.statusSegments.filter((s) => s.countsTowardRate !== false)
  const arcSegments = chartable.length > 0 ? chartable : summary.statusSegments
  const total = arcSegments.reduce((sum, s) => sum + s.count, 0) || summary.totalRecords || 1

  const cx = 58
  const cy = chartHeight / 2
  const outerR = 42
  const innerR = 26

  let startAngle = -Math.PI / 2
  for (const seg of arcSegments) {
    const slice = (seg.count / total) * Math.PI * 2
    if (slice <= 0) continue
    const theme = getStatusTheme(seg.label, 'light')
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.arc(cx, cy, outerR, startAngle, startAngle + slice)
    ctx.closePath()
    ctx.fillStyle = theme.color
    ctx.fill()
    startAngle += slice
  }

  ctx.beginPath()
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  ctx.fillStyle = '#212529'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const center = resolveRingCenter({
    segments: summary.statusSegments,
    presentPercent: summary.presentPercent,
    totalRecords: summary.totalRecords,
    headlineLabel: summary.headlineLabel,
  })
  ctx.font = 'bold 13px Helvetica, Arial, sans-serif'
  ctx.fillText(center.value, cx, cy - 4)
  ctx.font = '9px Helvetica, Arial, sans-serif'
  ctx.fillStyle = '#6c757d'
  ctx.fillText(center.label, cx, cy + 10)

  const barStartX = 118
  const maxBarW = 220
  let barY = BAR_TOP
  const barH = BAR_H
  const gap = BAR_GAP

  // The bars show every status, including the ones outside the rate, so they need their own scale:
  // measuring an Unmarked bar against the donut's denominator can exceed it and overflow the track.
  const barScale = Math.max(...bars.map((s) => s.count), total, 1)

  for (const seg of bars) {
    const theme = getStatusTheme(seg.label, 'light')
    const barW = Math.max(4, (seg.count / barScale) * maxBarW)
    ctx.fillStyle = theme.softBg
    ctx.fillRect(barStartX, barY, maxBarW, barH)
    ctx.fillStyle = theme.color
    ctx.fillRect(barStartX, barY, barW, barH)
    ctx.fillStyle = '#212529'
    ctx.font = '10px Helvetica, Arial, sans-serif'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillText(`${seg.label}: ${seg.count}`, barStartX + maxBarW + 8, barY + barH / 2)
    barY += barH + gap
  }

  ctx.fillStyle = '#212529'
  ctx.font = 'bold 10px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(`Total records: ${exportTotalRecords(summary)}`, barStartX, barY + barH / 2)

  return canvas.toDataURL('image/png')
}
